import { createAsyncThunk } from "@reduxjs/toolkit";
import { AxiosError, isAxiosError } from "axios";
import { displayAlert } from "../slice/alertSlice.tsx";
import brandApi from "../api/brandApi.tsx";
import { handleThunkError } from "./handleThunkError";
import {
  BrandCreateDataType,
  BrandDeleteDataType,
  BrandEditByIdDataType,
  BrandItemType,
  SwitchBrandBodyType,
} from "../types/brandTypes.tsx";
import { setNewCurrentBrand } from "../slice/brandSlice.tsx";
import { clearPersonaById, clearPersonas } from "../slice/personasSlice.tsx";
import { clearCampaignById, clearCampaigns } from "../slice/campaignsSlice.tsx";
import { clearBriefById, clearBriefs } from "../slice/creatorBriefsSlice.tsx";
import { RootState } from "../root.ts";
import {
  deleteRevmaticsCookie,
  setRevmaticsCookie,
} from "../../helpers/cookie/revmaticsCookies.ts";
import { getCookieName } from "../../helpers/cookieName/cookieName.ts";

interface ApiSimpleObjectError {
  [key: string]: string[][];
}

export const fetchBrandList = createAsyncThunk(
  "brand/fetchBrandList",
  async (_, thunkAPI) => {
    try {
      const response = await brandApi.fetchBrandsList();
      const brandList = response.data.brands;

      const state = thunkAPI.getState() as RootState;
      const currentBrand = state.brand.currentBrand;

      // 1. If there are no brands at all → clear everything
      if (!brandList.length) {
        thunkAPI.dispatch(setNewCurrentBrand(null));
        deleteRevmaticsCookie(getCookieName.currentBrand);
        return brandList;
      }

      // 2. Check if current brand still exists in the list
      const isCurrentBrandValid = currentBrand
        ? brandList.some(
            (item: BrandItemType) =>
              item.id.toString() === currentBrand.toString(),
          )
        : false;

      // 3. If current brand is valid → do nothing
      if (isCurrentBrandValid) {
        return brandList;
      }

      // 4. Pick next brand:
      //    - first try "is_active" from backend
      //    - fallback to first item in list
      const nextBrand =
        brandList.find((item: BrandItemType) => item.is_active)?.id ??
        brandList[0]?.id;

      // 5. Apply new brand context
      const nextBrandStr = nextBrand.toString();

      thunkAPI.dispatch(setNewCurrentBrand(nextBrandStr));
      thunkAPI.dispatch(switchBrand({ brand_id: nextBrandStr }));

      // 6. Sync cross-app storage
      setRevmaticsCookie(getCookieName.currentBrand, nextBrandStr);

      return brandList;
    } catch (e: unknown) {
      handleThunkError(e, thunkAPI.dispatch, "An unexpected error occurred.");
    }
  },
);

export const fetchBrandById = createAsyncThunk(
  "brand/fetchBrandById",
  async (id: string, thunkAPI) => {
    try {
      const response = await brandApi.fetchBrandById(id);
      return response.data;
    } catch (e: unknown) {
      handleThunkError(e, thunkAPI.dispatch, "An unexpected error occurred.");
    }
  },
);

export const editBrandById = createAsyncThunk(
  "brand/editBrandById",
  async (data: BrandEditByIdDataType, thunkAPI) => {
    try {
      const response = await brandApi.editBrandById(data);
      if (data.callback) {
        data.callback();
      }

      thunkAPI.dispatch(
        displayAlert({
          type: "success",
          text: "Data was edited successfully",
        }),
      );
      return response.data;
    } catch (e: unknown) {
      if (isAxiosError(e) && e.response?.status === 429)
        return thunkAPI.rejectWithValue("Rate limited.");
      if (e instanceof AxiosError && e.response && e.response.data) {
        const data = e.response.data as ApiSimpleObjectError;
        Object.keys(data).forEach((key: string) =>
          thunkAPI.dispatch(
            displayAlert({
              type: "error",
              text: data[key][0].join(". "),
            }),
          ),
        );
      }
    }
  },
);
export const createBrand = createAsyncThunk(
  "brand/createBrand",
  async (data: BrandCreateDataType, thunkAPI) => {
    try {
      const response = await brandApi.createBrand(data.body);
      if (data.callback) {
        data.callback();
      }
      thunkAPI.dispatch(
        displayAlert({
          type: "success",
          text: "Brand was created successfully",
        }),
      );
      return response.data;
    } catch (e: unknown) {
      if (isAxiosError(e) && e.response?.status === 429)
        return thunkAPI.rejectWithValue("Rate limited.");
      if (e instanceof AxiosError && e.response && e.response.data) {
        const data = e.response.data as ApiSimpleObjectError;
        Object.keys(data).forEach((key: string) =>
          thunkAPI.dispatch(
            displayAlert({
              type: "error",
              text:
                typeof data[key] === "string"
                  ? data[key]
                  : data[key].join(". "),
            }),
          ),
        );
      }
    }
  },
);

export const deleteBrand = createAsyncThunk(
  "brand/deleteBrand",
  async (data: BrandDeleteDataType, thunkAPI) => {
    try {
      await brandApi.deleteBrand(data.id);
      if (data.callback) {
        data.callback();
      }
      thunkAPI.dispatch(
        displayAlert({
          type: "success",
          text: `${data.name} was deleted successfully`,
        }),
      );
      return data.id;
    } catch (e: unknown) {
      if (isAxiosError(e) && e.response?.status === 429)
        return thunkAPI.rejectWithValue("Rate limited.");
      if (e instanceof AxiosError && e.response && e.response.data) {
        const data = e.response.data as ApiSimpleObjectError;
        Object.keys(data).forEach((key: string) =>
          thunkAPI.dispatch(
            displayAlert({
              type: "error",
              text:
                typeof data[key] === "string"
                  ? data[key]
                  : data[key].join(". "),
            }),
          ),
        );
      }
    }
  },
);

export const switchBrand = createAsyncThunk(
  "brand/switchBrand",
  async (body: SwitchBrandBodyType, thunkAPI) => {
    try {
      const response = await brandApi.switchBrand(body);
      thunkAPI.dispatch(setNewCurrentBrand(response.data.brand_id.toString()));
      setRevmaticsCookie(
        getCookieName.currentBrand,
        response.data.brand_id.toString(),
      );
      thunkAPI.dispatch(clearPersonas());
      thunkAPI.dispatch(clearPersonaById());
      thunkAPI.dispatch(clearCampaigns());
      thunkAPI.dispatch(clearCampaignById());
      thunkAPI.dispatch(clearBriefs());
      thunkAPI.dispatch(clearBriefById());
      return response.data;
    } catch (e: unknown) {
      handleThunkError(e, thunkAPI.dispatch, "An unexpected error occurred.");
    }
  },
);

export const completeOnboardingThunk = createAsyncThunk<
  void,
  string,
  { state: RootState; rejectValue: string }
>("brand/completeOnboarding", async (brandId, { rejectWithValue }) => {
  try {
    await brandApi.completeOnboarding(brandId);
  } catch (e: unknown) {
    console.error("Failed to mark onboarding as complete:", e);
    const error = e as AxiosError;
    return rejectWithValue(error.message || "Failed to complete onboarding");
  }
});
